import { Calendar } from 'lucide-react'
import { formatDate, formatDateTime } from '@/lib/locale'
import { LIFECYCLE_ORDER, describeStatus } from './status'
import type { ContactCategory, ContactMessageStatus } from './types'

/**
 * Minimal shape needed to render the timeline. Both the customer-side
 * ContactQueryRow and the admin-side ContactMessageRow satisfy this.
 */
export interface TimelineMessage {
  category: ContactCategory
  status: ContactMessageStatus
  viewed_at: string | null
  handled_at: string | null
  target_release_date: string | null
  decision_note: string | null
}

const fmtDateTime = (v: string | null) => (v ? formatDateTime(v) : null)

// Reuses the bk-timeline-* keyframes already shipped in styles/globals.css
// for the invoice-detail timeline. Stagger constants match invoice page so
// the rhythm feels identical across the app.
const STEP_MS = 180

export function StatusTimeline({ message }: { message: TimelineMessage }) {
  const desc = describeStatus(message.status, message.category)
  const reachedOrder = desc.order

  // Build the ordered list of items to render. The lifecycle (requested →
  // viewed → in_progress) always renders in full so the customer sees the
  // whole journey. If the message reached a terminal/decision state it's
  // appended as a 4th item.
  const items: Array<{
    key: string
    status: ContactMessageStatus
    isTerminal: boolean
  }> = LIFECYCLE_ORDER.map((s) => ({ key: s, status: s, isTerminal: false }))

  if (desc.terminal || desc.order >= 4) {
    items.push({ key: `terminal-${message.status}`, status: message.status, isTerminal: true })
  }

  return (
    <div className="space-y-4">
      <ol
        // Re-keying on the visible status forces the animation to replay
        // when the customer-side polling brings in a new state - the user
        // gets a visual cue that something just changed.
        key={`status-${message.status}`}
        className="text-sm"
      >
        {items.map((item, idx) => {
          const d = describeStatus(item.status, message.category)
          const reached = item.isTerminal || reachedOrder >= d.order
          const Icon = d.icon
          const dotDelay = idx * STEP_MS
          const lineDelay = idx * STEP_MS + 90
          const textDelay = idx * STEP_MS + 40
          const isLast = idx === items.length - 1

          // Subline content per item - surfaces the real timestamps the
          // backend records so the user sees when each transition happened.
          let subline: string | null = null
          if (item.status === 'viewed' && message.viewed_at) {
            subline = fmtDateTime(message.viewed_at)
          } else if (item.isTerminal && message.handled_at) {
            subline = fmtDateTime(message.handled_at)
          }

          return (
            <li key={item.key} className="relative flex gap-3 pb-4 last:pb-0">
              {/* Connector line - anchored to the <li> (not the dot column)
                  so it runs through the pb-4 gap into the next item without
                  being clipped. left-3.5 = w-7 / 2 keeps it under the
                  dot's centre. top:32px = dot.mt-0.5 (2) + size-7 (28) + 2px
                  breathing room below the dot. bottom:-2px extends 2px into
                  the next <li> so the line meets that dot's top exactly. */}
              {!isLast ? (
                <span
                  className="bk-timeline-line absolute left-3.5 -ml-px w-px bg-muted-foreground/40"
                  style={{ top: '32px', bottom: '-2px', animationDelay: `${lineDelay}ms` }}
                />
              ) : null}
              <div className="flex w-7 shrink-0 flex-col items-center">
                <span
                  className={`bk-timeline-dot relative z-10 mt-0.5 flex size-7 items-center justify-center rounded-pill border ${
                    reached
                      ? item.isTerminal && d.badgeVariant === 'danger'
                        ? 'border-destructive bg-destructive text-destructive-foreground'
                        : 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-card text-muted-foreground'
                  }`}
                  style={{ animationDelay: `${dotDelay}ms` }}
                >
                  <Icon className="size-3.5" />
                </span>
              </div>
              <div
                className="bk-timeline-text min-w-0 flex-1 pt-0.5"
                style={{ animationDelay: `${textDelay}ms` }}
              >
                <p className={`text-sm font-medium ${reached ? '' : 'text-muted-foreground'}`}>
                  {d.label}
                </p>
                {item.isTerminal && d.blurb ? (
                  <p className="text-[11px] text-muted-foreground">{d.blurb}</p>
                ) : null}
                {subline ? (
                  <p className="text-[11px] text-muted-foreground">{subline}</p>
                ) : null}
              </div>
            </li>
          )
        })}
      </ol>

      {/* "Target release" only renders for categories where a release
          actually ships: bug fixes and feature requests. Sales and support
          have no release concept, so we never surface it for them even if
          a stale value lingers in the DB. */}
      {message.target_release_date && (message.category === 'bug' || message.category === 'feature') ? (
        <div className="flex items-start gap-2 rounded-md border bg-primary/5 p-3 text-sm">
          <Calendar className="size-4 shrink-0 text-primary" />
          <div>
            <p className="font-medium">Target release</p>
            <p className="text-xs text-muted-foreground">
              {formatDate(message.target_release_date)}
            </p>
          </div>
        </div>
      ) : null}

      {message.decision_note ? (
        <div className="rounded-md border bg-muted/30 p-3 text-sm">
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Note from Bokapsys
          </p>
          <p className="whitespace-pre-wrap text-sm">{message.decision_note}</p>
        </div>
      ) : null}
    </div>
  )
}
